import type { CompanyLogoMime } from '../../data/database.types';
import {
  selectImageRaster,
  type EncodeMime,
  type ImageRasterPort,
} from '../../platform/image-raster';

/**
 * Turning whatever Brandon picked into a small company mark — M10 / S10-03A.
 *
 * **THIS IS NOT THE CAPTURE EVIDENCE PIPELINE, AND IT MUST NOT BECOME ONE.** A
 * logo has no Work Log, no Before/After class, no archive, no removal history
 * and no delivery queue. It borrows exactly one thing from M5: the generic
 * `ImageRasterPort`, which decodes and re-encodes images and knows nothing
 * about Captures. Everything else here is its own.
 *
 * **RE-ENCODING IS THE POINT, NOT AN OPTIMISATION.** Storing the picked file
 * would mean storing whatever the phone produced — a 12-megapixel photograph of
 * a business card, with its metadata, for a mark that prints an inch wide. One
 * decode-and-re-encode makes the size ceiling something the product ENFORCES
 * rather than requests, and strips whatever came attached along the way.
 *
 * **SVG IS REFUSED.** It is markup, not a picture: it can carry script and
 * external references, and it renders unpredictably in whatever PDF engine
 * arrives in S10-04. Every company logo is available as a raster file, so
 * refusing costs Brandon nothing and removes the surface entirely.
 */

/** The long edge of the stored mark. A report header prints it about an inch wide. */
export const LOGO_LONG_EDGE = 600;

/**
 * The ceiling on what may be handed to the raster port, in bytes.
 *
 * **A GUARD AGAINST DECODING, NOT AGAINST STORING.** An accidental 40 MB
 * panorama should be refused before a phone tries to decode it, which is a
 * different and earlier failure than the stored-size limit below.
 */
export const LOGO_MAX_SOURCE_BYTES = 12 * 1024 * 1024;

/**
 * The ceiling on the STORED mark, in base64 characters — mirrored by the
 * database constraint in migration 0016, which is the authority.
 *
 * ~300,000 characters is about 220 KB of image: comfortably above the ~200 KB
 * target so base64's own overhead cannot push a legitimate logo over, and
 * nowhere near a multi-megabyte allowance.
 */
export const LOGO_MAX_BASE64_LENGTH = 300_000;

export const ALLOWED_LOGO_MIMES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp'];

export interface PreparedLogo {
  readonly mime: CompanyLogoMime;
  readonly base64: string;
  readonly width: number;
  readonly height: number;
}

export type LogoRefusal =
  | 'not-an-image'
  | 'svg-refused'
  | 'unsupported-type'
  | 'too-large-to-read'
  | 'unreadable'
  | 'still-too-large';

export type PreparedLogoResult =
  | { readonly ok: true; readonly logo: PreparedLogo }
  | { readonly ok: false; readonly refusal: LogoRefusal };

/**
 * WebP can be decoded but the raster port only ENCODES png and jpeg, so a WebP
 * logo is re-encoded as PNG. That is the honest mapping rather than a silent
 * mislabel: what gets stored is what the type says it is.
 */
function storedMimeFor(sourceMime: string): { source: EncodeMime; stored: CompanyLogoMime } {
  if (sourceMime === 'image/jpeg') return { source: 'image/jpeg', stored: 'image/jpeg' };
  return { source: 'image/png', stored: 'image/png' };
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  // Chunked: `String.fromCharCode(...bytes)` on a few hundred kilobytes blows
  // the argument limit, and it does it only on large files — the exact ones a
  // logo limit exists to handle.
  const CHUNK = 0x8000;
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK));
  }
  return btoa(binary);
}

/**
 * Decode, shrink and re-encode a picked file into a stored mark.
 *
 * **REFUSALS ARE VALUES, NOT EXCEPTIONS.** Every one of them is something
 * Brandon did, not something that broke, and each has a sentence he can act on.
 */
export async function prepareCompanyLogo(
  bytes: Uint8Array,
  declaredMime: string,
  raster: ImageRasterPort = selectImageRaster(),
): Promise<PreparedLogoResult> {
  const mime = declaredMime.toLowerCase().trim();

  // Checked BEFORE the allow-list so the message can name the real reason.
  if (mime === 'image/svg+xml' || mime === 'image/svg') {
    return { ok: false, refusal: 'svg-refused' };
  }
  if (!mime.startsWith('image/')) return { ok: false, refusal: 'not-an-image' };
  if (!ALLOWED_LOGO_MIMES.includes(mime)) return { ok: false, refusal: 'unsupported-type' };
  if (bytes.length > LOGO_MAX_SOURCE_BYTES) return { ok: false, refusal: 'too-large-to-read' };

  const { source, stored } = storedMimeFor(mime);

  let rendered;
  try {
    // **THE FILE MUST ACTUALLY DECODE.** A `.png` containing a text file passes
    // every check above; it fails here, which is why the extension is never
    // trusted on its own.
    rendered = await raster.render({
      bytes,
      sourceMime: source,
      // A logo is not a camera photograph and carries no meaningful EXIF
      // orientation. Passing null means nothing is rotated.
      orientation: null,
      longEdge: LOGO_LONG_EDGE,
      encodeMime: stored === 'image/jpeg' ? 'image/jpeg' : 'image/png',
      quality: 0.92,
    });
  } catch {
    return { ok: false, refusal: 'unreadable' };
  }

  const base64 = bytesToBase64(rendered.bytes);
  // A pathological image can still exceed the ceiling after shrinking — a
  // photograph re-encoded as PNG, for instance. Refusing beats silently
  // storing something the database would reject anyway.
  if (base64.length > LOGO_MAX_BASE64_LENGTH) return { ok: false, refusal: 'still-too-large' };

  return {
    ok: true,
    logo: { mime: stored, base64, width: rendered.width, height: rendered.height },
  };
}

/** What the Settings screen shows when a logo is refused. Never a code, never a stack. */
export function describeLogoRefusal(refusal: LogoRefusal): string {
  switch (refusal) {
    case 'svg-refused':
      return 'SVG logos are not supported. Please choose a PNG, JPEG or WebP file.';
    case 'not-an-image':
    case 'unsupported-type':
      return 'That file is not a supported image. Please choose a PNG, JPEG or WebP file.';
    case 'too-large-to-read':
      return 'That image is too large to use as a logo. Please choose a smaller file.';
    case 'unreadable':
      return 'That image could not be read. Please choose a different file.';
    case 'still-too-large':
      return 'That image is still too large after resizing. Please choose a simpler logo.';
  }
}

/** `data:image/png;base64,…` for an `<img>`. The stored value carries no prefix. */
export function logoDataUrl(mime: CompanyLogoMime, base64: string): string {
  return `data:${mime};base64,${base64}`;
}
